import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-lg border border-cy/15 bg-qcard px-4 py-3 text-sm text-slate-200 placeholder:text-slate-600 outline-none transition-colors duration-300 focus:border-cy/60";

const emptyForm = { name: "", company: "", email: "", phone: "", service: "", message: "", website: "" };

const QuoteModal = ({ open, onOpenChange }) => {
  const { t } = useLanguage();
  const [form, setForm] = useState(emptyForm);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, form);
      onOpenChange(false);
      toast.success(t.quote.success);
      setForm(emptyForm);
    } catch {
      toast.error(t.quote.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="quote-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[95] flex items-center justify-center bg-ink/85 p-4 backdrop-blur-md"
          onClick={() => onOpenChange(false)}
          data-testid="quote-modal-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: 56, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.97 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-xl border border-cy/25 bg-panel p-8 shadow-[0_0_80px_rgba(0,240,255,0.15)] sm:p-10"
            data-testid="quote-modal"
          >
            <button
              data-testid="quote-modal-close"
              onClick={() => onOpenChange(false)}
              className="absolute right-5 top-5 grid h-9 w-9 place-items-center rounded-full border border-cy/20 text-slate-400 transition-colors hover:border-cy hover:text-cy"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>

            <p className="font-mono text-xs uppercase tracking-[0.25em] text-cy">// QC-QUOTE</p>
            <h3 className="mt-3 font-display text-2xl font-bold text-slate-50 sm:text-3xl">{t.quote.title}</h3>
            <p className="mt-2 text-sm text-slate-400">{t.quote.sub}</p>

            <form onSubmit={submit} className="mt-8 grid gap-4">
              <input
                type="text"
                name="website"
                value={form.website}
                onChange={set("website")}
                tabIndex={-1}
                autoComplete="off"
                className="hidden"
                aria-hidden="true"
              />
              <div className="grid gap-4 sm:grid-cols-2">
                <input data-testid="quote-form-name" required value={form.name} onChange={set("name")} placeholder={t.quote.name} className={inputCls} />
                <input data-testid="quote-form-company" required value={form.company} onChange={set("company")} placeholder={t.quote.company} className={inputCls} />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <input data-testid="quote-form-email" required type="email" value={form.email} onChange={set("email")} placeholder={t.quote.email} className={inputCls} />
                <input data-testid="quote-form-phone" value={form.phone} onChange={set("phone")} placeholder={t.quote.phone} className={inputCls} />
              </div>
              <select
                data-testid="quote-form-service"
                required
                value={form.service}
                onChange={set("service")}
                className={`${inputCls} appearance-none ${form.service ? "" : "text-slate-600"}`}
              >
                <option value="" disabled>
                  {t.quote.servicePlaceholder}
                </option>
                {t.services.items.map((s) => (
                  <option key={s.id} value={s.id} className="bg-qcard text-slate-200">
                    {s.title}
                  </option>
                ))}
              </select>
              <textarea
                data-testid="quote-form-message"
                required
                rows={4}
                value={form.message}
                onChange={set("message")}
                placeholder={t.quote.messagePlaceholder}
                className={`${inputCls} resize-none`}
              />
              <button
                data-testid="quote-form-submit"
                type="submit"
                disabled={sending}
                className="group mt-2 flex items-center justify-center gap-2 rounded-full bg-cy px-8 py-4 font-display text-base font-bold text-ink transition-all duration-300 hover:bg-em hover:shadow-[0_0_35px_rgba(0,240,255,0.4)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {sending ? t.quote.sending : t.quote.submit}
                {sending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                )}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default QuoteModal;
